import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';

import { isEmpty, isEqual, cloneDeep, orderBy } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import Chip from '@material-ui/core/Chip';
import BobCancelButton from '../../../components/BobCancelButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import AutoComplete from '@material-ui/lab/Autocomplete';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormHelperText from '@material-ui/core/FormHelperText';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { SplitButton } from '@progress/kendo-react-buttons';
import { Mixpanel } from '../../../helpers/mixpanel';

import { Splitter } from '@progress/kendo-react-layout';

import * as ReportingActions from '../../../actions/reportingActions';
import * as ConfigurationActions from '../../../actions/configurationActions';
import * as DashboardActions from '../../../actions/dashboardActions';

import * as classes from './ReportConfigurationCreate.module.css';
import ReportTabPane from './ReportTabPane';
import OptimizationReportTabPane from './OptimizationReportTabPane';
import { runTimeDataList } from './RunTimeData';

const MIXPANEL_EVENT = 'Inspection Report Create';
// const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const isRunTimeDataValid = (data) => {
    if (isEmpty(data)) return false;

    let isValid = false;
    data.forEach(item => {
        if (item.start || item.end) {
            isValid = true;
        } 
    });
    return isValid;
};

const isObjectsToMonitorRequired = (runtimeMonitor, runTimeData, occupancySensors) => {
    let isRequired = false;

    if (runtimeMonitor === 'Sensor' && !isEmpty(occupancySensors)) {
        isRequired = true;
    }
    else if (runtimeMonitor === 'Manual' && isRunTimeDataValid(runTimeData)) {
        isRequired = true;
    }
    return isRequired;
};

const ValidationSchema = Yup.object().shape({
    description: Yup.string()
        .required(),
    client: Yup.string()
        .required(),
    site: Yup.string()
        .required(),
    equipmentType: Yup.array(),
    equipment:     Yup.array()
        .when('system', {
            is:   (value) => isEmpty(value),
            then: Yup.array().required()
        }),
    system: Yup.array()
        .when('equipment', {
            is:   (value) => isEmpty(value),
            then: Yup.array().required()
        }),
    timeRange: Yup.string()
        .required(),
    spaceAirSensors:   Yup.array(),
    outsideAirSensors: Yup.array(),
    runtimeMonitor:    Yup.string(),
    runTimeData:       Yup.array()
        .when(['objectsToMonitor', 'runtimeMonitor'], {
            is:        (objectsToMonitor, runtimeMonitor ) => runtimeMonitor === 'Manual' && !isEmpty(objectsToMonitor),
            then:      Yup.array().required(),
            otherwise: Yup.array().notRequired()
        }),
    occupancySensors: Yup.array()
        .when(['objectsToMonitor', 'runtimeMonitor'], {
            is:        (objectsToMonitor, runtimeMonitor) => runtimeMonitor === 'Sensor' && !isEmpty(objectsToMonitor),
            then:      Yup.array().required(),
            otherwise: Yup.array().notRequired()
        }),
    objectsToMonitor: Yup.array()
        .when(['runtimeMonitor', 'runTimeData', 'occupancySensors'], {
            is:        (runtimeMonitor, runTimeData, occupancySensors) => isObjectsToMonitorRequired(runtimeMonitor, runTimeData, occupancySensors),
            then:      Yup.array().required(),
            otherwise: Yup.array().notRequired()
        })
}, [
    ['equipment', 'system'], 
    ['client', 'site'], 
    ['spaceAirSensors', 'outsideAirSensors'], 
    ['runTimeData', 'objectsToMonitor'],
    ['runTimeData', 'runtimeMonitor'],
    ['occupancySensors', 'objectsToMonitor'],
    ['occupancySensors', 'runtimeMonitor'],
    ['objectsToMonitor', 'runtimeMonitor'],
    ['objectsToMonitor', 'runTimeData'],
    ['objectsToMonitor', 'occupancySensors']
]);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const timeRanges = [
    'Monthly',
    'Today',
    'Week to Day',
    'Month to Day',
    'Quarter to Day',
    'Previous Day',
    'Previous Week',
    'Previous Month',
    'Previous Quarter',
    'Custom Date'
];

const optimizationTimeRanges = [
    'Previous 24 Hours',
    'Previous 7 Days',
    'Previous 14 Days',
    'Custom Date'
];

const MenuProps = {
    disablePortal:      true,
    getContentAnchorEl: () => null,
    PaperProps:         {
        style: {
            maxHeight: 500
        },
    },
};

const DEFAULT_FORM_VALUES = {
    description:      'The following building performance report was created utilizing BOB to pull data from the Building Automation System (BAS) to analyze for opportunities for improvements to overall building performance and energy efficiency. This study included an analysis of selected equipment/systems. This report describes the service and maintenance issues found as well as presents opportunities for improvements to the building to help reduce energy costs and improve building performance.',
    client:           '',
    site:             '',
    equipmentType:    [],
    equipment:        [],
    system:           [],
    timeRange:        '',
    targetYear:       moment().year(),
    targetDate:       moment().month(),
    occupancySensors: [],
    objectsToMonitor: [],
    runTimeData:      [],
    runtimeMonitor:   'Sensor'
};

const buttonItems = [
    {
        text: 'Generate'
    }, 
    {
        text: 'Save & Generate',
    }
];

const initialDataStateFilter = {
    logic:   'and',
    filters: []
};

const rulesTabInitialDataState = {
    filter: initialDataStateFilter,
    sort:   [],
    skip:   0
};

const runTimeTabInitialDataState = {
    filter: [],
    sort:   [],
    take:   10,
    skip:   0
};

const ReportConfigurationCreate = ({
    activeConfiguration,
    activeNavCompany,
    bobLoading,
    createInspectionReport,
    createOptimizationReport,
    currentUser,
    displayErrorMessage,
    fetchConfigurations,
    fetchEntityRules,
    fetchReportSites,
    fetchReportEquipment,
    fetchReportEquipmentTypes,
    fetchReportSensors,
    fetchReportSystems,
    isClearAppliedConfiguration,
    handleBackClick,
    navClients,
    newConfiguration,
    reportType,
    setActiveConfiguration,
    setActiveReportConfiguration,
    saveConfiguration,
    setEntityRules,
    setIsClearAppliedConfiguration,
    setShowSaveConfigurationModal,
    setWorkBookMode,
    updateConfiguration,
    updateConfigurationData,
    workbookModeEnabled,
    ...props
}) => {
    const [loading, setLoading] = useState(false);
    const [sitesLoading, setSitesLoading] = useState(false);
    const [equipmentLoading, setEquipmentLoading] = useState(false);
    const [showPrevious, setShowPrevious] = useState(false);

    const [clientList, setClientList] = useState([]);
    const [siteList, setSiteList] = useState([]);
    const [equipmentList, setEquipmentList] = useState([]);
    const [systemList, setSystemList] = useState([]);
    const [equipmentTypeList, setEquipmentTypeList] = useState([]);
    const [staticEquipment, setStaticEquipment] = useState([]);

    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [maxDate, setMaxDate] = useState(moment().endOf('day').toDate());
    const [ruleStartDate, setRuleStartDate] = useState();
    const [ruleEndDate, setRuleEndDate] = useState();
    const [ruleTargetMonth, setRuleTargetMonth] = useState(-1);
    const [ruleTargetYear, setRuleTargetYear] = useState(-1);

    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [currentYear, setCurrentYear] = useState();
    const [currentMonth, setCurrentMonth] = useState();

    const [selectedRules, setSelectedRules] = useState([]);
    const [prevStartDate, setPrevStartDate] = useState();
    const [prevEndDate, setPrevEndDate] = useState();
    const [prevEquipment, setPrevEquipment] = useState([]);
    const [prevSystem, setPrevSystem] = useState([]);
    const [selectedState, setSelectedState] = useState({});
    const [selectedStateItems, setSelectedStateItems] = useState({});
    const [selectedSpaceAirSensors, setSelectedSpaceAirSensors] = useState([]);
    const [selectedOutsideAirSensors, setSelectedOutsideAirSensors] = useState([]);
    const [selectedOccupanyStatusSensors, setSelectedOccupanyStatusSensors] = useState([]);
    const [entities, setEntities] = useState([]);
    const [runTimeData, setRunTimeData] = useState(cloneDeep(runTimeDataList));
    const [saveConfigurationType, setSaveConfigurationType] = useState('');
    const [ignoreGenerate, setIgnoreGenerate] = useState(false);

    const [objectsToMonitor, setObjectsToMonitor] = useState([]);
    const [aggregate, setAggregate] = useState('none');
    const [occupancyAggregate, setOccupancyAggregate] = useState('none');
    const [reportFilter, setReportFilter] = useState();
    const [initialValues, setInitialValues] = useState({...DEFAULT_FORM_VALUES});
    const [optimizationSensorsOrder, setOptimizationSensorsOrder] = useState([]);
    const [selectedOptimizationTab, setSelectedOptimizationTab] = useState(0);

    const [rulesTabDataState, setRulesTabDataState] = useState(rulesTabInitialDataState);
    const [runTimeTabDataState, setRunTimeTabDataState] = useState(runTimeTabInitialDataState);
    const [selectState, setSelectState] = useState({});
    const [activeRadioButton, setActiveRadioButton] = useState('Sensor');
    const [minFaultHours, setMinFaultHours] = useState(0);

    const clearAppliedConfiguration = () => {
        setActiveConfiguration({});

        setSiteList([]);
        setSitesLoading(false);

        setEquipmentTypeList([]);
        setEquipmentList([]);
        setSystemList([]);
        setEquipmentLoading(false);
        
        let range = reportType === 'optimization' ? 'Previous 24 Hours' : '';

        formik.setFieldValue('timeRange', range, true);
        formik.setFieldValue('client', '', true);
        formik.setFieldValue('site', '', true);
        formik.setFieldValue('equipment', [], true);
        formik.setFieldValue('system', [], true);
        formik.setFieldValue('equipmentType', [], true);

        if (reportType === 'inspection') {
            setShowPrevious(false);
            setRuleStartDate('');
            setRuleEndDate('');

            setPrevEquipment([]);
            setPrevSystem([]);

            setSelectedSpaceAirSensors([]);
            setSelectedOutsideAirSensors([]);
            setRunTimeData(runTimeDataList);
            setAggregate('none');

            setRulesTabDataState(rulesTabInitialDataState);
            setSelectedState({});
            setSelectedStateItems({});

            setRunTimeTabDataState(runTimeTabInitialDataState);
            setSelectState({});
            setActiveRadioButton('Sensor');

            setSelectedOccupanyStatusSensors({});
        }
    };

    useEffect(() => {
        // component unmount time
        return () => {
            clearAppliedConfiguration();
        };
    },[]);

    // this is for clearing applied configuration
    useEffect(() => {
        const clear =  () => {
            clearAppliedConfiguration();
            setIsClearAppliedConfiguration(false);
        };
    
        if (isClearAppliedConfiguration) clear();
    }, [isClearAppliedConfiguration]);
        
    // useEffect(() => {
    //     setInitialValues({...DEFAULT_FORM_VALUES});
    //     // entities used in optimization report
    //     setEntities([]);
    //     formik.resetForm();
    // },[reportType]);

    useEffect(() => {
        let initialValues = { ...DEFAULT_FORM_VALUES };
        
        const init = async () => {
            setActiveConfiguration({});
            let configurationKey = reportType === 'optimization' ? `BOB|Report|OptimizationReport` : `BOB|Report|InspectionReport`;
            await fetchConfigurations(currentUser.id, activeNavCompany.id, configurationKey, false);
        };
        
        if (reportType && reportType === 'optimization') {
            initialValues.timeRange = 'Previous 24 Hours';
        } else {
            initialValues.timeRange = '';
        }
        
        init();
        setInitialValues({...initialValues});
        // entities used in optimization report
        setEntities([]);
        formik.resetForm();
    },[reportType]);

    // save or save as configuration
    useEffect(() => {
        const init =  async () => {
            let configurationType = reportType === 'optimization' ? 'Save Optimization Report' : 'Save Inspection Report';
            setSaveConfigurationType(configurationType);
            formik.handleSubmit();
        };
    
        if (!isEmpty(newConfiguration)) init();
    }, [newConfiguration]);
    
    useEffect(() => {
        const init =  async () => {
            let configurationType = reportType === 'optimization' ? 'Update Optimization Report' : 'Update Inspection Report';
            setSaveConfigurationType(configurationType);
            formik.handleSubmit();
        };

        if (!isEmpty(activeConfiguration) && updateConfigurationData) init();
    }, [updateConfigurationData]);

    useEffect(() => {
        const init = async () => {
            const { viewData } = activeConfiguration;

            formik.setFieldValue('timeRange', viewData.timeRange, true);

            await handleClientChange(null, viewData.selectedClient);
            await handleSiteChange(null, viewData.site);
            
            formik.setFieldValue('equipmentType', viewData.equipmentType || [], true);
            formik.setFieldValue('equipment', viewData.selectedEquipment, true);
            formik.setFieldValue('system', viewData.selectedSystems, true);

            if (reportType === 'inspection') {
                setShowPrevious(viewData.showPrevious);
                setRuleStartDate(viewData.ruleStartDate);
                setRuleEndDate(viewData.ruleEndDate);

                await updateEntities(viewData.selectedEquipment, viewData.selectedSystems, moment(viewData.ruleStartDate).format(), moment(viewData.ruleEndDate).format(), viewData.site.id);

                setPrevEquipment(viewData.selectedEquipment);
                setPrevSystem(viewData.selectedSystems);

                setSelectedSpaceAirSensors(viewData.selectedSpaceAirSensors);
                setSelectedOutsideAirSensors(viewData.selectedOutsideAirSensors);
                setRunTimeData(viewData.runTimeData);
                setAggregate(viewData.aggregate);

                if (viewData.rulesTab.rulesTabDataState)
                    setRulesTabDataState(viewData.rulesTab.rulesTabDataState);
                
                if (viewData.rulesTab?.selectedState)
                    setSelectedState(viewData.rulesTab.selectedState);

                if (viewData.rulesTab?.minfaulthours) {
                    setMinFaultHours(viewData.rulesTab.minfaulthours);
                }
                else {
                    setMinFaultHours(0);
                }

                if (viewData.rulesTab?.selectedStateItems)
                    setSelectedStateItems(viewData.rulesTab.selectedStateItems);

                if (viewData.runTimeTab?.runTimeTabDataState)
                    setRunTimeTabDataState(viewData.runTimeTab.runTimeTabDataState);

                if (viewData.runTimeTab?.selectState)
                    setSelectState(viewData.runTimeTab.selectState);

                if (viewData.runTimeTab?.activeRadioButton)
                    setActiveRadioButton(viewData.runTimeTab.activeRadioButton);

                if (viewData.runTimeTab?.selectedOccupanyStatusSensors)
                    setSelectedOccupanyStatusSensors(viewData.runTimeTab.selectedOccupanyStatusSensors);

                if (viewData.runTimeTab?.workbookModeEnabled)
                    setWorkBookMode(viewData.workbookModeEnabled);
            }
            else if (reportType === 'optimization') {
                setOptimizationSensorsOrder(viewData.optimizationSensorsOrder);
            }
        };

        if (!isEmpty(activeConfiguration))  init();
    },[activeConfiguration && activeConfiguration.id]);

    useEffect(() => {
        formik.setFieldValue('spaceAirSensors', selectedSpaceAirSensors, []);
        formik.setFieldValue('outsideAirSensors', selectedOutsideAirSensors, []);
    }, [selectedSpaceAirSensors, selectedOutsideAirSensors]);

    useEffect(() => {
        formik.setFieldValue('objectsToMonitor', objectsToMonitor, []);
    }, [objectsToMonitor]);

    useEffect(() => {
        formik.setFieldValue('occupancySensors', selectedOccupanyStatusSensors, []);
    }, [selectedOccupanyStatusSensors]);

    useEffect(() => {
        const selectedRunTimeHours = getOccupiedHoursList(runTimeData);
        formik.setFieldValue('runTimeData', selectedRunTimeHours);
    }, [runTimeData]);

    useEffect(() => {
        formik.setFieldValue('runtimeMonitor', activeRadioButton);
    }, [activeRadioButton]);

    useEffect(() => {
        const thisYear = moment().year();
        const yearList = [thisYear, thisYear - 1, thisYear - 2];
        const monthList = moment.months();
        const thisMonth = moment().month();
        const legalMonths = [];
        monthList.forEach((target, index) => {
            if (index <= thisMonth) {
                legalMonths.push({
                    name: target,
                    index
                });

                if (index === thisMonth) {
                    setCurrentMonth(index);
                }
            }
        });

        setCurrentYear(thisYear);
        setYears(yearList);
        setMonths(legalMonths);
    }, []);


    useEffect(() => {
        if (navClients && isEmpty(clientList)) setClientList(navClients);
    }, [navClients]);

    useEffect(() => {
        return () => {
            setSelectedStateItems({});
            setSelectedState({});
        };
    }, []);

    const getConditionalFormats = (conditionalFormats) => {
        let validFormats = [];

        conditionalFormats.forEach(item => {
            if (item.condition && item.operator) {
                if (item.operator === 'Between') {
                    if (item.field1 && item.field2) {
                        validFormats.push({
                            operator: item.operator.toLowerCase(),
                            min:      item.field1,
                            max:      item.field2,
                            color:    item.condition.toLowerCase()
                        });
                    }
                } else {
                    if (item.field1) {
                        let operator = item.operator.toLowerCase();
                        if ( operator === 'greater than' || operator === 'less than') {
                            operator = operator.split(' ');
                            operator = operator.join('');
                        }

                        validFormats.push({
                            operator: operator.toLowerCase(),
                            min:      item.field1,
                            max:      null,
                            color:    item.condition.toLowerCase()
                        });
                    }
                }
            }
        });

        return validFormats;
    };

    const getEntityIdsPayload = (data) => {
        let entitiesData = [];
        // remove entity if it does not have sensors selected
        data.forEach(entity => {
            let sensorList = entity.sensors ? entity.sensors.filter(sensor => !sensor.available) : [];
            if (!isEmpty(sensorList)) {
                entitiesData.push(entity);
            }
        });

        if (!isEmpty(entitiesData)) {
            let equipments = [];
            let systems = [];
            entitiesData.forEach(item => {
                let entityIdsObj = {
                    'EntityId':  item.id,
                    'sensorIds': []
                };

                // check first to see if the entity exists in the optimizationSensorsOrderArray
                let sensorOrder = optimizationSensorsOrder.find(so => so.entityId === item.id)?.orderedSensors;

                if (!sensorOrder) {
                    sensorOrder = optimizationSensorsOrder.find(so => so.equipmentTypeId === item.equipmentType.id)?.orderedSensors;
                }
                sensorOrder = orderBy(sensorOrder, 'order', 'asc');

                let selectedSensors = item.sensors ? item.sensors.filter(sensor => !sensor.available) : [];

                if (!isEmpty(selectedSensors)) {
                    let selectedSensorList = [];
                    // put them in the correct order
                    const updatedList = selectedSensors.map((sensor, idx) => {
                        let itemSensor = sensorOrder.find(so => so.id === sensor.id);
                        if (itemSensor) {
                            sensor.order = itemSensor.order;
                        }
                        else {
                            itemSensor = sensorOrder.find(so => so.sensorType.id === sensor.sensorType.id);
                            if (itemSensor) {
                                sensor.order = itemSensor.order;
                            }
                        }
                        return sensor;
                    });

                    const orderedSensorsList = orderBy(updatedList, 'order','asc');
                    orderedSensorsList.forEach((sensor, idx) => {
                        let conditionalFormats = sensor.conditionalFormats ? getConditionalFormats(sensor.conditionalFormats) : [];
                        selectedSensorList.push({
                            'Index':            idx,
                            'SensorId':         sensor.id,
                            'CustomFormatting': conditionalFormats
                        });
                    });
                    entityIdsObj.sensorIds = selectedSensorList;
                }

                if (item.isSystem) {
                    systems.push(entityIdsObj);
                } else {
                    equipments.push(entityIdsObj);
                }
            });

            return {
                tempEquipmentIds: equipments,
                tempSystemIds:    systems
            };
        }
    };
    
    const getOccupiedHoursList = () => {
        let timeList = [];
        runTimeData.forEach((item, id) => {
            // ignoe alldays index - 0
            if (id !== 0) {
                if (item.selected) {
                    item.timeList.forEach(time => {
                        if (time.from && time.to) {
                            timeList.push({
                                'dow':   id - 1,
                                'start': moment(time.from, 'HHmm').format('hh:mm A'),
                                'end':   moment(time.to, 'HHmm').format('hh:mm A')
                            });
                        }
                    });
                } 
            }
        });

        return timeList;
    };

    const formik = useFormik({
        initialValues:      initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {

            // Run Time tab time slot validation
            if (document.getElementById('time-slots-has-error') !== null) {
                return;
            }

            // optimization report entity shoud have a selected sensor validation
            // if (reportType && reportType === 'optimization') {
            //     let isValid = validateOptimizationPayload();
            //     if (!isValid) {
            //         return;
            //     }
            // }

            const body = {
                clientName:          values.client.name,
                description:         values.description,
                siteId:              values.site.id,
                siteName:            values.site.name,
                reportType:          'inspection',
                targetDate:          {},
                priorDate:           {},
                ruleId:              [],
                minfaulthours:       minFaultHours,
                outsideAirSensorIds: [],
                spaceAirSensorIds:   [],
                //OccupiedHours:       []
            };


            if (!isEmpty(values.system)) body.systemIds = values.system.filter(item => item.id !== 0).map(item => item.id);
            if (!isEmpty(values.equipment)) body.equipmentIds = values.equipment.filter(item => item.id !== 0).map(item => item.id);
            if (!isEmpty(values.equipmentType)) body.equipmentTypes = values.equipmentType.filter(item => item.id !== 0).map(item => item.id);

            if (!isEmpty(objectsToMonitor)) {
                body.ObjectsToMonitor = {
                    aggregate,
                    objects: []
                };

                objectsToMonitor.forEach(item => {
                    body.ObjectsToMonitor.objects.push({ entityId: item.entityId, sensorId: item.id });
                });
            }

            if (!isEmpty(selectedStateItems)) {
                const ruleIds = Object.keys(selectedStateItems);
                body.ruleId = ruleIds;
            }

            let spaceAirSensors = [];
            let outsideAirSensors = [];

            if (!isEmpty(selectedOutsideAirSensors)) {
                outsideAirSensors = [...selectedOutsideAirSensors].filter(item => item.sensorid !== 0).map(item => item.sensorid);
                body.outsideAirSensorIds = outsideAirSensors;
            }

            if (!isEmpty(selectedSpaceAirSensors)) {
                spaceAirSensors = [...selectedSpaceAirSensors].filter(item => item.sensorid !== 0).map(item => item.sensorid);
                body.spaceAirSensorIds = spaceAirSensors;
            }

            let diff = 0;
            if (startDate && endDate) {
                diff = moment(endDate).add(1, 'day').endOf('day').diff(moment(startDate).startOf('day'), 'days');
            }

            switch (values.timeRange) {
                case 'Today':
                    body.targetDate.start = moment().startOf('day').valueOf();
                    body.targetDate.end = moment().local().valueOf();

                    body.priorDate.start = moment().subtract(1, 'day').startOf('day').valueOf();
                    body.priorDate.end = moment().subtract(1, 'day').endOf('day').valueOf();
                    break;
                case 'Week to Day':
                    body.targetDate.start = moment().startOf('week').valueOf();
                    body.targetDate.end = moment().local().valueOf();

                    body.priorDate.start = moment().subtract(1, 'week').startOf('week').valueOf();
                    body.priorDate.end = moment().subtract(1, 'week').endOf('day').valueOf();
                    break;
                case 'Month to Day':
                    body.targetDate.start = moment().startOf('month').valueOf();
                    body.targetDate.end = moment().local().valueOf();

                    body.priorDate.start = moment().subtract(1, 'month').startOf('month').valueOf();
                    body.priorDate.end = moment().subtract(1, 'month').endOf('day').valueOf();
                    break;
                case 'Quarter to Day':
                    body.targetDate.start = moment().startOf('quarter').valueOf();
                    body.targetDate.end = moment().local().valueOf();

                    body.priorDate.start = moment().subtract(1, 'quarter').startOf('quarter').valueOf();
                    body.priorDate.end = moment().subtract(1, 'quarter').endOf('day').valueOf();
                    break;
                case 'Previous Day':
                    body.targetDate.start = moment().subtract(1, 'day').startOf('day').valueOf();
                    body.targetDate.end = moment().subtract(1, 'day').endOf('day').valueOf();

                    body.priorDate.start = moment().subtract(2, 'day').startOf('day').valueOf();
                    body.priorDate.end = moment().subtract(2, 'day').endOf('day').valueOf();
                    break;
                case 'Previous Week':
                    body.targetDate.start = moment().subtract(1, 'week').startOf('week').valueOf();
                    body.targetDate.end = moment().subtract(1, 'week').endOf('week').valueOf();

                    body.priorDate.start = moment().subtract(2, 'week').startOf('week').valueOf();
                    body.priorDate.end = moment().subtract(2, 'week').endOf('week').valueOf();
                    break;
                case 'Previous Month':
                    body.targetDate.start = moment().subtract(1, 'month').startOf('month').valueOf();
                    body.targetDate.end = moment().subtract(1, 'month').endOf('month').endOf('day').valueOf();

                    body.priorDate.start = moment().subtract(2, 'month').startOf('month').valueOf();
                    body.priorDate.end = moment().subtract(2, 'month').endOf('month').endOf('day').valueOf();
                    break;
                case 'Previous Quarter':
                    body.targetDate.start = moment().subtract(1, 'quarter').startOf('quarter').valueOf();
                    body.targetDate.end = moment().subtract(1, 'quarter').endOf('quarter').valueOf();

                    body.priorDate.start = moment().subtract(2, 'quarter').startOf('quarter').valueOf();
                    body.priorDate.end = moment().subtract(2, 'quarter').endOf('quarter').valueOf();
                    break;
                case 'Monthly':
                    body.targetDate.start = moment().month(values.targetDate).year(values.targetYear).startOf('month').valueOf();

                    if (moment().month(values.targetDate).year(values.targetYear).endOf('month').isAfter(moment())) {
                        body.targetDate.end = moment().endOf('day').valueOf();
                    }
                    else {
                        body.targetDate.end = moment().month(values.targetDate).year(values.targetYear).endOf('month').valueOf();
                    }

                    body.priorDate.start = moment().month(values.targetDate).year(values.targetYear).subtract(1, 'month').startOf('month').valueOf();
                    body.priorDate.end = moment().month(values.targetDate).year(values.targetYear).subtract(1, 'month').endOf('month').valueOf();
                    break;
                case 'Custom Date':
                    body.targetDate.start = moment(startDate).startOf('day').valueOf();
                    body.targetDate.end = moment(endDate).endOf('day').valueOf();

                    body.priorDate.start = moment(startDate).subtract(diff, 'days').startOf('day').valueOf();
                    body.priorDate.end = moment(endDate).subtract(diff, 'days').endOf('day').valueOf();
                    break;
                case 'Previous 7 Days':
                    body.startDate = moment().subtract(7, 'day').startOf('day').local();
                    body.endDate = moment().local();
                    break;
                case 'Previous 14 Days':
                    body.startDate = moment().subtract(14, 'day').startOf('day').local();
                    body.endDate = moment().local();
                    break;
                case 'Previous 24 Hours':
                    body.startDate = moment().subtract(1, 'day').local();
                    body.endDate = moment().local();
                    break;
                default:
                    break;
            }

            try {
                setLoading(true);
                Mixpanel.track(MIXPANEL_EVENT, {
                    company:  activeNavCompany.name,
                    userName: `${currentUser.firstName} ${currentUser.lastName}`,
                    email:    currentUser.email
                });

                let recentlySavedConfigurationId = null;

                // Optimization Report
                if (reportType && reportType === 'optimization') {
                    let data = getEntityIdsPayload(entities);

                    const optimizationPayload = {
                        companyId:      activeNavCompany.id,
                        clientId:       values.client.id,
                        siteId:         values.site.id,
                        equipmentIds:   data.tempEquipmentIds,
                        equipmentTypes: body.equipmentTypes,
                        systemIds:      data.tempSystemIds,
                        systemTypes:    [],
                        startDate:      body.startDate,
                        endDate:        body.endDate
                    };
                    
                    optimizationPayload.filter = !isEmpty(reportFilter) ? reportFilter : null;
                    if (!isEmpty(reportFilter))  optimizationPayload.filter = reportFilter;
                    if (values.timeRange === 'Custom Date') {
                        optimizationPayload.startDate = moment(startDate).local();
                        if (moment().dayOfYear() === moment(endDate).dayOfYear()) {
                            optimizationPayload.endDate = moment().local();
                        }
                        else {
                            optimizationPayload.endDate = moment(endDate).endOf('day').local();
                        }
                    }

                    let configurationData = {
                        optimizationPayload,
                        viewType:          `Optimization Report`,
                        ownerId:           currentUser.id,
                        timeRange:         values.timeRange,
                        site:              values.site,
                        selectedClient:    formik.values.client,
                        equipmentType:     formik.values.equipmentType,
                        selectedEquipment: formik.values.equipment,
                        selectedSystems:   formik.values.system,
                        entities,
                        optimizationSensorsOrder
                    };

                    if (saveConfigurationType === 'Save Optimization Report') {
                        const { viewName, sharedEmails } = { ...newConfiguration };

                        const payload =  {
                            'viewKey':      `BOB|Report|OptimizationReport`,
                            'entityId':     activeNavCompany.id,
                            'entityName':   activeNavCompany.name,
                            'name':         viewName,
                            'isDefault':    false,
                            'viewData':     JSON.stringify(configurationData),
                            'sharedEmails': sharedEmails
                        };
                        
                        recentlySavedConfigurationId = await saveConfiguration(currentUser.id, payload);
                        setSaveConfigurationType('');
                    } else if (saveConfigurationType === 'Update Optimization Report') {
                        let payload = { ...activeConfiguration };

                        payload.viewData = JSON.stringify(configurationData);

                        await updateConfiguration(activeConfiguration.id, payload);

                        let updatedPayload = { ...payload };

                        updatedPayload.viewData = configurationData;
                        setActiveConfiguration(updatedPayload);
                    }

                    if (!ignoreGenerate)
                        await createOptimizationReport(currentUser.id, optimizationPayload, showPrevious);
                } else {
                    delete body.equipmentTypes;
                    //body.OccupiedHours = getOccupiedHoursList(runTimeData);
                    //const runTimeSensorIds = !isEmpty(selectedOccupanyStatusSensors) ? isArray(selectedOccupanyStatusSensors) ? selectedOccupanyStatusSensors.filter(v => v.sensorid !== '0').map(v => v.sensorid) : [selectedOccupanyStatusSensors.sensorid] : [];
                    //body.runTimeSensorIds = !isEmpty(runTimeSensorIds) ? runTimeSensorIds : [];
                    
                    //if (runTimeSensorIds.length > 1) {
                    //    body.occupancyAggregate = occupancyAggregate.toLowerCase();
                    //}

                    let configurationData = {
                        body,
                        viewType:          `Inspection Report`,
                        ownerId:           currentUser.id,
                        timeRange:         values.timeRange,
                        site:              values.site,
                        selectedClient:    formik.values.client,
                        equipmentType:     formik.values.equipmentType,
                        selectedEquipment: formik.values.equipment,
                        selectedSystems:   formik.values.system,
                        //objectsToMonitor:  objectsToMonitor,
                        rulesTab:          {
                            rulesTabDataState,
                            selectedState,
                            selectedStateItems,
                            minfaulthours: minFaultHours
                        }, 
                        // runTimeTab: {
                        //     activeRadioButton,
                        //     selectState,
                        //     runTimeTabDataState,
                        //     selectedOccupanyStatusSensors
                        // },
                        showPrevious,
                        runTimeData,
                        aggregate,
                        ruleStartDate,
                        ruleEndDate,
                        workbookModeEnabled
                    };

                    if (!isEmpty(selectedSpaceAirSensors)) {
                        let selectedSensors = selectedSpaceAirSensors.filter(s => s.sensorId !== 0);
                        if (!isEmpty(selectedSensors)) {
                            configurationData.selectedSpaceAirSensors = selectedSpaceAirSensors;
                        }
                    }

                    if (!isEmpty(selectedOutsideAirSensors)) {
                        let selectedSensors = selectedOutsideAirSensors.filter(s => s.sensorId !== 0);
                        if (!isEmpty(selectedSensors)) {
                            configurationData.selectedOutsideAirSensors = selectedOutsideAirSensors;
                        }
                    }

                    // if (!isEmpty(selectedOccupanyStatusSensors)) {
                    //     let selectedSensors = isArray(selectedOccupanyStatusSensors) ? selectedOccupanyStatusSensors.filter(s => s.sensorId !== 0) : [selectedOccupanyStatusSensors];
                    //     if (!isEmpty(selectedSensors)) {
                    //         configurationData.runTimeTab.selectedOccupanyStatusSensors = selectedOccupanyStatusSensors;
                    //     }
                    // }

                    if (saveConfigurationType === 'Save Inspection Report') {
                        const { viewName, sharedEmails } = { ...newConfiguration };

                        const payload =  {
                            'viewKey':      `BOB|Report|InspectionReport`,
                            'entityId':     activeNavCompany.id,
                            'entityName':   activeNavCompany.name,
                            'name':         viewName,
                            'isDefault':    false,
                            'viewData':     JSON.stringify(configurationData),
                            'sharedEmails': sharedEmails
                        };
                        
                        recentlySavedConfigurationId = await saveConfiguration(currentUser.id, payload);
                        setSaveConfigurationType('');
                    } else if (saveConfigurationType === 'Update Inspection Report') {
                        let payload = { ...activeConfiguration };

                        payload.viewData = JSON.stringify(configurationData);

                        await updateConfiguration(activeConfiguration.id, payload);

                        let updatedPayload = { ...payload };

                        updatedPayload.viewData = configurationData;
                        setActiveConfiguration(updatedPayload);
                    }

                    if (!ignoreGenerate)
                        await createInspectionReport(body, showPrevious);
                }
                
                setLoading(false);
                if (!ignoreGenerate) {
                    handleBackClick(); 
                } else {
                    let configurationKey = reportType === 'optimization' ? `BOB|Report|OptimizationReport` : `BOB|Report|InspectionReport`;
                    await fetchConfigurations(currentUser.id, activeNavCompany.id, configurationKey, false);

                    // this is used in reports page only
                    if (recentlySavedConfigurationId)
                        setActiveReportConfiguration(recentlySavedConfigurationId);

                    setIgnoreGenerate(false);
                }
            } catch (error) {
                setLoading(false);
            }
        }
    });

    const handleCancel = () => {
        handleBackClick();
    };

    const [nestedPanes] = useState([
        { resizable: false },
        { resizable: false }
    ]);

    const [panes, setPanes] = useState([
        {size: '550px', resizable: false}, {}
    ]);

    useEffect(() => {
        if (reportType && reportType === 'optimization') {
            setPanes([
                {size: '400px', resizable: false}, {}
            ]);
        } else {
            setPanes([
                {size: '550px', resizable: false}, {}
            ]);
        }
    },[reportType]);

    useEffect(() => {
        let updatedEquipmentList = [...staticEquipment].filter(item => item.id !== 0);

        const filter = formik.values.equipmentType.filter(item => item.id !== 0);
        if (!isEmpty(formik.values.equipmentType)) {
            const filtered = updatedEquipmentList.filter(item => filter.find(object => item.equipmentType.id === object.id));
            updatedEquipmentList = filtered;
        } else {
            updatedEquipmentList = staticEquipment;
        }

        if (!isEmpty(updatedEquipmentList) && !updatedEquipmentList.find(item => item.id === 0)) updatedEquipmentList.unshift({ id: 0, name: 'All' });
        setEquipmentList(updatedEquipmentList);
    }, [formik.values.equipmentType]);

    // selected Equipments + selected Systems
    useEffect(() => {
        let systemIds = formik.values.system;

        systemIds.forEach(item => {
            item.isSystem = true;
        });

        let entities = [ ...formik.values.equipment, ...systemIds ];
        // ignored this - { id: 0, name: 'All' }
        entities = entities.filter(entity => entity.id !== 0 );
        
        setEntities(entities);
    },[formik.values.equipment, formik.values.system]);

    const setTimeRangeValues = timeRange => {
        let ruleStart = moment();
        let ruleEnd = moment();
        switch (timeRange) {
            case 'Today':
                ruleStart = moment().startOf('day').valueOf();
                ruleEnd = moment().endOf('day').valueOf();
                break;
            case 'Week to Day':
                ruleStart = moment().startOf('week').valueOf();
                ruleEnd = moment().endOf('day').valueOf();
                break;
            case 'Month to Day':
                ruleStart = moment().startOf('month').valueOf();
                ruleEnd = moment().endOf('day').valueOf();
                break;
            case 'Quarter to Day':
                ruleStart = moment().startOf('quarter').valueOf();
                ruleEnd = moment().endOf('day').valueOf();
                break;
            case 'Previous Day':
                ruleStart = moment().subtract(1, 'day').startOf('day').valueOf();
                ruleEnd = moment().subtract(1, 'day').endOf('day').valueOf();
                break;
            case 'Previous Week':
                ruleStart = moment().subtract(1, 'week').startOf('week').valueOf();
                ruleEnd = moment().subtract(1, 'week').endOf('week').valueOf();
                break;
            case 'Previous Month':
                ruleStart = moment().subtract(1, 'month').startOf('month').valueOf();
                ruleEnd = moment().subtract(1, 'month').endOf('month').endOf('day').valueOf();
                break;
            case 'Previous Quarter':
                ruleStart = moment().subtract(1, 'quarter').startOf('quarter').valueOf();
                ruleEnd = moment().subtract(1, 'quarter').endOf('quarter').valueOf();
                break;
            case 'Monthly':
                ruleStart = moment().month(formik.values.targetDate).year(formik.values.targetYear).startOf('month').valueOf();
                ruleEnd = moment().month(formik.values.targetDate).year(formik.values.targetYear).endOf('month').valueOf();
                break;
            case 'Custom Date':
                ruleStart = moment(startDate).startOf('day').valueOf();
                ruleEnd = moment(endDate).endOf('day').valueOf();
                break;
            case 'Previous 7 Days':
                ruleStart = moment().subtract(7, 'day').startOf('day').format('YYYY-MM-DD');
                ruleEnd = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
                break;
            case 'Previous 14 Days':
                ruleStart = moment().subtract(14, 'day').startOf('day').format('YYYY-MM-DD');
                ruleEnd = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
                break;
            case 'Previous 24 Hours':
                ruleStart = moment().subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
                ruleEnd = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
                break;
            default:
                break;
        }
        setRuleStartDate(ruleStart);
        setRuleEndDate(ruleEnd);
    };

    const handleTimeRangeChange = (event, timeRange) => {
        formik.setFieldValue('timeRange', timeRange, true);
        setTimeRangeValues(timeRange);
    };

    const handleClientChange = async (event, client) => {
        setSelectedState({});
        setSelectedStateItems({});
        setEntityRules([], {});

        if (!client) {
            formik.setFieldValue('client', '', true);
            formik.setFieldValue('equipment', [], true);
            formik.setFieldValue('system', [], true);
            formik.setFieldValue('equipmentType', [], true);
            return;
        }

        formik.setFieldValue('client', client, true);
        setSitesLoading(true);
        const sites = await fetchReportSites(client.id);
        setSiteList(sites);
        setSitesLoading(false);

        formik.setFieldValue('equipment', [], true);
        formik.setFieldValue('site', '', true);
        formik.setFieldValue('system', [], true);
        formik.setFieldValue('equipmentType', [], true);
    };

    const handleSiteChange = async (event, site) => {
        setSelectedState({});
        setSelectedStateItems({});
        setEntityRules([], {});

        if (!site) {
            formik.setFieldValue('site', '', true);
            formik.setFieldValue('equipment', [], true);
            formik.setFieldValue('system', [], true);
            formik.setFieldValue('equipmentType', [], true);
            return;
        }

        formik.setFieldValue('site', site, true);
        setEquipmentLoading(true);
        let equipment = await fetchReportEquipment(site.id);
        equipment = orderBy(equipment, [equip => equip.name.toLowerCase()]);
        setStaticEquipment(equipment);

        if (!isEmpty(equipment)) {
            equipment.unshift({ id: 0, name: 'All' });
        }

        let systems = await fetchReportSystems(site.id);
        systems = orderBy(systems, [system => system.name.toLowerCase()]);

        if (!isEmpty(systems)) {
            systems.unshift({ id: 0, name: 'All' });
        }

        let equipmentTypes = await fetchReportEquipmentTypes(site.id);

        equipmentTypes = orderBy(equipmentTypes, [type => type.name.toLowerCas]);

        if (!isEmpty(equipmentTypes)) {
            equipmentTypes.unshift({ id: 0, name: 'All' });
        }

        setEquipmentTypeList(equipmentTypes);
        setEquipmentList(equipment);
        setSystemList(systems);
        setEquipmentLoading(false);

        formik.setFieldValue('equipment', [], true);
        formik.setFieldValue('system', [], true);
        formik.setFieldValue('equipmentType', [], true);
    };

    const handleEquipmentChange = (event, values) => {
        if (event.target.textContent === 'All' || event.target.value === '0') {
            if (formik.values.equipment.some(item => item.id === 0)) {
                formik.setFieldValue('equipment', [], true);
            } else {
                formik.setFieldValue('equipment', equipmentList, true);
            }
        } else {
            if (values.length !== equipmentList.length) {
                let updatedValues = [...values];
                updatedValues = updatedValues.filter(item => item.id !== 0);
                formik.setFieldValue('equipment', updatedValues, true);
            } else {
                formik.setFieldValue('equipment', values, true);
            }
        }
    };

    const handleSystemChange = (event, values) => {
        if (event.target.textContent === 'All' || event.target.value === '0') {
            if (formik.values.system.some(item => item.id === 0)) {
                formik.setFieldValue('system', [], true);
            } else {
                formik.setFieldValue('system', systemList, true);
            }
        } else {
            if (values.length !== systemList.length) {
                let updatedValues = [...values];
                updatedValues = updatedValues.filter(item => item.id !== 0);
                formik.setFieldValue('system', updatedValues, true);
            } else {
                formik.setFieldValue('system', values, true);
            }
        }
    };

    const handleEquipmentTypeChange = (event, values) => {
        if (event.target.textContent === 'All' || event.target.value === '0') {
            if (formik.values.equipmentType.some(item => item.id === 0)) {
                formik.setFieldValue('equipmentType', [], true);
            } else {
                formik.setFieldValue('equipmentType', equipmentTypeList, true);
            }
        } else {
            if (values.length !== equipmentTypeList.length) {
                let updatedValues = [...values];
                updatedValues = updatedValues.filter(item => item.id !== 0);
                formik.setFieldValue('equipmentType', updatedValues, true);
            } else {
                formik.setFieldValue('equipmentType', values, true);
            }
        }
    };

    const renderTimeRangeHelperText = () => {
        let start = null;
        let end = null;

        if (formik.values.timeRange === 'Monthly') {
            start = moment().month(formik.values.targetDate).year(formik.values.targetYear).subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
            end = moment().month(formik.values.targetDate).year(formik.values.targetYear).subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Today') {
            start = moment().subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
            end = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Week to Day') {
            start = moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
            end = moment().subtract(1, 'week').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Month to Day') {
            start = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
            end = moment().subtract(1, 'month').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Quarter to Day') {
            start = moment().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD');
            end = moment().subtract(1, 'quarter').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Previous Day') {
            start = moment().subtract(2, 'day').startOf('day').format('YYYY-MM-DD');
            end = moment().subtract(2, 'day').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Previous Week') {
            start = moment().subtract(2, 'week').startOf('week').format('YYYY-MM-DD');
            end = moment().subtract(2, 'week').endOf('week').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Previous Month') {
            start = moment().subtract(2, 'month').startOf('month').format('YYYY-MM-DD');
            end = moment().subtract(2, 'month').endOf('month').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Previous Quarter') {
            start = moment().subtract(2, 'quarter').startOf('quarter').format('YYYY-MM-DD');
            end = moment().subtract(2, 'quarter').endOf('quarter').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Custom Date') {
            let diff = 0;
            if (startDate && endDate) {
                diff = moment(endDate).add(1, 'day').endOf('day').diff(moment(startDate).startOf('day'), 'days');
            }

            start = moment(startDate).subtract(diff, 'days').startOf('day').format('YYYY-MM-DD');
            end = moment(endDate).subtract(diff, 'days').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Previous 7 Days') {
            start = moment().subtract(7, 'day').startOf('day').format('YYYY-MM-DD');
            end = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Previous 14 Days') {
            start = moment().subtract(14, 'day').startOf('day').format('YYYY-MM-DD');
            end = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        } else if (formik.values.timeRange === 'Previous 24 Hours') {
            start = moment().subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
            end = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        }

        if (formik.values.timeRange === 'Custom Date') {
            if (startDate && endDate) {
                return <span className={classes.PreviousPeriod}>{`Previous Period: ${start} - ${end}`}</span>;
            } else {
                return <span className={classes.PreviousPeriod}>{`Previous Period: Please select your custom date range`}</span>;
            }
        } else if (start && end) {
            return <span className={classes.PreviousPeriod}>{`Previous Period: ${start} - ${end}`}</span>;
        }
    };

    const handleTargetYearChange = event => {
        const { value } = event.target;

        const monthList = moment.months();

        if (moment().year() === value) {
            const legalMonths = [];
            const { targetDate } = formik.values;

            monthList.forEach((target, index) => {
                if (index <= currentMonth) {
                    legalMonths.push({
                        name: target,
                        index
                    });
                }
            });

            if (targetDate > currentMonth) {
                formik.setFieldValue('targetDate', currentMonth, false);
            }

            setMonths(legalMonths);
        } else {
            const allMonths = [];

            monthList.forEach((target, index) => {
                allMonths.push({
                    name: target,
                    index
                });
            });

            setMonths(allMonths);
        }

        formik.handleChange(event);
    };

    const handleTargetDateChange = event => {
        const { value } = event.target;

        formik.setFieldValue('targetDate', value, false);
    };

    const updateEntities = async (equipment, systems, fromDate, toDate, siteId) => {
        let entityIds = [];
        let equipmentIds = [];
        let systemIds = [];

        equipment.forEach(item => {
            if (item.id === 0) return;
            entityIds.push(item.id);
            equipmentIds.push(item.id);
        });

        systems.forEach(item => {
            if (item.id === 0) return;
            entityIds.push(item.id);
            systemIds.push(item.id);
        });

        if (reportType !== 'optimization' && !isEmpty(entityIds)) {
            await fetchEntityRules(entityIds, moment(fromDate).format(), moment(toDate).format(), siteId);
            await fetchReportSensors(systemIds, equipmentIds);
        } else {
            setEntityRules([], {});
        }
        setSelectedState({});
        setSelectedStateItems({});
    };

    const handleDatePickerChange = update => {
        const startDate = moment(update[0]);
        let end = moment().endOf('day');
        
        if (!isEmpty(update[1])) {
            end = moment(update[1]).endOf('day') ;
        }

        if (moment(startDate.add(3, 'months')).isAfter(moment().endOf('day'))) {
            end = moment().endOf('day');
        }
        else {
            end = moment(startDate).add(3, 'months');
        }
        
        setMaxDate(moment(end).toDate());

        setDateRange(update);
        setRuleStartDate(update[0]);
        setRuleEndDate(moment(end).endOf('day').format());
    };

    const handleTargetBlur = async (e) => {
        let month, year;
        let targetStartDate, targetEndDate;

        switch (e.target.name) {
            case 'targetDate':
                year = formik.values.targetYear;
                formik.setFieldValue(e.target.name, e.target.value, true);
                targetStartDate = moment().month(e.target.value).year(year).startOf('month').valueOf();
                targetEndDate = moment().month(e.target.value).year(year).endOf('month').valueOf();
                setRuleStartDate(targetStartDate);
                setRuleEndDate(targetEndDate);
                break;
            case 'targetYear':
                month = formik.values.targetDate;
                formik.setFieldValue(e.target.name, e.target.value, true);
                targetStartDate = moment().month(month).year(e.target.value).startOf('month').valueOf();
                targetEndDate = moment().month(month).year(e.target.value).endOf('month').valueOf();
                setRuleStartDate(targetStartDate);
                setRuleEndDate(targetEndDate);
                break;
            default:
                
        }
        formik.setFieldValue(e.target.name, e.target.value, true);
    }; 

    const handleBlur = async (e) => {
        let targetDateChange = false;
        if (!isEmpty(e) && e.target.name === 'targetMonth') {
            if (!isEqual(ruleTargetMonth, e.target.value)) {
                setRuleTargetMonth(e.target.value);
                targetDateChange = true;
            }
        }
        else if (!isEmpty(e) && e.targetName === 'targetYear') {
            if (!isEqual(ruleTargetYear, e.target.value)) {
                setRuleTargetYear(e.target.value);
                targetDateChange = true;
            }
        }

        if (!isEqual(prevStartDate, ruleStartDate) || !isEqual(prevEndDate, ruleEndDate)) {
            setPrevStartDate(ruleStartDate);
            setPrevEndDate(ruleEndDate);
            targetDateChange = true;
        }

        if ((!isEqual(prevEquipment, formik.values.equipment) || !isEqual(prevSystem, formik.values.system) || targetDateChange)  && formik.values.timeRange !== '') {
            
            await updateEntities(formik.values.equipment, formik.values.system, ruleStartDate, ruleEndDate, formik.values.site.id);

            setPrevEquipment(formik.values.equipment);
            setPrevSystem(formik.values.system);
        }
    };

    const handleDeleteChip = async (option, type) => {
        let systems = [];
        let equipment = [];

        if (type === 'equipment') {
            let updatedEquipment = [...formik.values.equipment];
            const index = updatedEquipment.findIndex(item => item.id === option.id);
            updatedEquipment.splice(index, 1);

            equipment = [...updatedEquipment];
            systems = [...formik.values.system];
            formik.setFieldValue('equipment', updatedEquipment, false);
        } else {
            let updatedSystem = [...formik.values.system];
            const index = updatedSystem.findIndex(item => item.id === option.id);
            updatedSystem.splice(index, 1);

            equipment = [...formik.values.equipment];
            systems = [...updatedSystem];
            formik.setFieldValue('system', updatedSystem, false);
        }

        await updateEntities(equipment, systems);

        setPrevEquipment(equipment);
        setPrevSystem(systems);
    };

    const handleButtonClick = () => {
        setIgnoreGenerate(true);
        if (isEmpty(activeConfiguration)) {
            let configurationType = reportType === 'optimization' ? 'Save Optimization Report' : 'Save Inspection Report';
            setSaveConfigurationType(configurationType);
            setShowSaveConfigurationModal(true);
        } else {
            let configurationType = reportType === 'optimization' ? 'Update Optimization Report' : 'Update Inspection Report';
            setSaveConfigurationType(configurationType);
            formik.handleSubmit();
        }
    };

    const handleButtonItemClick = e => {
        if ( reportType === 'optimization') {
            // max 255 tabs allowed on the spreadsheet
            if (entities.length > 255 ) {
                displayErrorMessage('Selected Equipment + System should be less than 256');
                return;
            } 
        } 
        
        if (e.item.text === 'Save & Generate') {
            let configurationType = reportType === 'optimization' ? 'Save Optimization Report' : 'Save Inspection Report';
            setSaveConfigurationType(configurationType);
            setShowSaveConfigurationModal(true);
        } else {
            if (saveConfiguration !== '') {
                setSaveConfigurationType('');
            }
            formik.handleSubmit();
        }
    };

    const setSelectedEquipments = equipments => {
        formik.setFieldValue('equipment', equipments, false);
    };

    const setSelectedSystems = systems => {
        formik.setFieldValue('system', systems, false);
    };

    return (
        <React.Fragment>
            <style>
                {
                    `.k-splitter-flex .k-pane-static {
                        flex-grow: 0;
                        flex-shrink: 0;
                        flex-basis: fit-content;
                        
                    }
                    .k-pane .k-scrollable {
                        height: 100% !important;
                    }
                    .react-datepicker__close-icon {
                        top: -12px;
                    }
                    `
                }
            </style>
            <Splitter
                panes={nestedPanes}
                orientation={'vertical'}
            >
                <div>
                    <Splitter panes={panes}>
                        <div className={classes.FormPane}>
                            {
                                reportType !== 'optimization' ?
                                    <FormControl className={classes.TopInputWrapper}>
                                        <TextField
                                            id="description"
                                            label="Description"
                                            name="description"
                                            multiline
                                            maxRows={10}
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            variant="outlined"
                                            InputProps={{
                                                onBlur:  formik.handleBlur,
                                                classes: {
                                                    root: classes.DescriptionTextArea
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    : <p>Choose the equipment and/or systems to include in the optimization report</p>
                            }
                            {
                                reportType !== 'optimization' ?
                                    <FormControl className={classes.TopInputWrapper}>
                                        <FormControlLabel
                                            classes={{
                                                label: classes.FormLabel
                                            }}
                                            onChange={() => setShowPrevious(!showPrevious)}
                                            value="top"
                                            control={<Checkbox checked={showPrevious} />}
                                            label="Show Period Comparison Grid"
                                            labelPlacement="end"
                                        />
                                    </FormControl>
                                    : null
                            }
                            <FormControl className={Boolean(formik.errors.timeRange) && Boolean(formik.touched.timeRange) ? classes.ErrorTimeRangeWrapper : classes.TimeRangeInputWrapper}>
                                <div className={classes.DateSelector}>
                                    <AutoComplete
                                        id="timeRange"
                                        name="timeRange"
                                        options={reportType && reportType === 'optimization' ? optimizationTimeRanges : timeRanges}
                                        getOptionLabel={(option) => option || ''}
                                        value={formik.values.timeRange}
                                        onChange={handleTimeRangeChange}
                                        onBlur={handleBlur}
                                        size="small"
                                        required
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                className={classes.Required}
                                                label="Time Range"
                                                variant="outlined"
                                                required={true}
                                                error={Boolean(formik.errors.timeRange) && Boolean(formik.touched.timeRange)}
                                                helperText={renderTimeRangeHelperText()}
                                            />}
                                    />
                                </div>
                                {
                                    formik.values.timeRange === 'Custom Date' ?
                                        <div className={classes.DatePicker}>
                                            <DatePicker
                                                popperPlacement="bottom"
                                                className={classes.DatePickerInputBox}
                                                popperClassName={classes.DatePickerPopper}
                                                selectsRange={true}
                                                startDate={startDate}
                                                endDate={endDate}
                                                onChange={(update) => {
                                                    handleDatePickerChange(update);
                                                }}
                                                maxDate={maxDate}
                                                minDate={new Date(moment().subtract(3, 'years'))}
                                                isClearable={true}
                                            />
                                        </div>
                                        :
                                        null
                                }
                            </FormControl>
                            {formik.values.timeRange === 'Monthly' ?
                                <FormControl className={classes.MonthRangeInputWrapper}>
                                    {
                                        <React.Fragment>
                                            <FormControl
                                                variant="outlined" size="small"
                                                className={classes.leftInputWrapper}
                                            >
                                                <InputLabel
                                                    htmlFor="targetYear"
                                                    classes={{
                                                        error:   classes.error,
                                                        focused: classes.labelFocused,
                                                        root:    classes.label
                                                    }}
                                                    error={Boolean(formik.errors.targetYear) && Boolean(formik.touched.targetYear)}
                                                >
                                                    Target Year
                                                </InputLabel>
                                                <Select
                                                    name="targetYear"
                                                    className={classes.select}
                                                    MenuProps={MenuProps}
                                                    id="targetYear"
                                                    value={formik.values.targetYear}
                                                    onChange={handleTargetYearChange}
                                                    inputProps={{
                                                        onBlur: handleTargetBlur
                                                    }}
                                                    error={Boolean(formik.errors.targetYear) && Boolean(formik.touched.targetYear)}
                                                >
                                                    {
                                                        years.map((item, index) =>
                                                            <MenuItem
                                                                key={index}
                                                                value={item}
                                                            >
                                                                {item}
                                                            </MenuItem>
                                                        )
                                                    }
                                                </Select>
                                                {formik.touched.targetYear &&
                                                    formik.errors.targetYear && <FormHelperText className={classes.error}>{formik.errors.targetYear}</FormHelperText>}
                                            </FormControl>
                                            <FormControl
                                                variant="outlined" size="small"
                                                className={classes.rightInputWrapper}
                                            >
                                                <InputLabel
                                                    htmlFor="targetDate"
                                                    classes={{
                                                        error:   classes.error,
                                                        focused: classes.labelFocused,
                                                        root:    classes.label
                                                    }}
                                                    error={Boolean(formik.errors.targetDate) && Boolean(formik.touched.targetDate)}
                                                >
                                                    Target Month
                                                </InputLabel>
                                                <Select
                                                    name="targetDate"
                                                    id="targetDate"
                                                    className={classes.select}
                                                    MenuProps={MenuProps}
                                                    value={formik.values.targetDate}
                                                    onChange={handleTargetDateChange}
                                                    inputProps={{
                                                        onBlur: handleTargetBlur
                                                    }}
                                                    error={Boolean(formik.errors.targetDate) && Boolean(formik.touched.targetDate)}
                                                >
                                                    {
                                                        months.map((item, index) =>
                                                            <MenuItem
                                                                key={index}
                                                                value={item.index}
                                                            >
                                                                {item.name}
                                                            </MenuItem>
                                                        )
                                                    }
                                                </Select>
                                                {formik.touched.targetDate &&
                                                    formik.errors.targetDate && <FormHelperText className={classes.error}>{formik.errors.targetDate}</FormHelperText>}
                                            </FormControl>
                                        </React.Fragment>
                                    }
                                </FormControl>
                                : <div /> }
                            <FormControl className={Boolean(formik.errors.client) && Boolean(formik.touched.client) ? classes.ErrorWrapper : classes.InputWrapper}>
                                <AutoComplete
                                    id="client"
                                    name="client"
                                    disabled={isEmpty(formik.values.timeRange) || (formik.values.timeRange === 'Custom Date' && (startDate === null || endDate === null))}
                                    options={orderBy(clientList, [client => client.name.toLowerCase()])}
                                    getOptionLabel={(option) => option.name || ''}
                                    getOptionSelected={(option, value) => option && value ? option.name === value.name : ''}
                                    value={formik.values.client}
                                    onChange={handleClientChange}
                                    onBlur={formik.handleBlur}
                                    size="small"
                                    required
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            className={classes.Required}
                                            label="Client"
                                            variant="outlined"
                                            required={true}
                                            error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                                            helperText={Boolean(formik.touched.client) && formik.errors.client}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loading ? <CircularProgress color="inherit" size={15} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />}
                                />
                            </FormControl>
                            <FormControl className={Boolean(formik.errors.site) && Boolean(formik.touched.site) ? classes.ErrorWrapper : classes.InputWrapper}>
                                <AutoComplete
                                    id="site"
                                    name="site"
                                    disabled={isEmpty(formik.values.client)}
                                    options={orderBy(siteList, [site => site.name.toLowerCase()])}
                                    getOptionLabel={(option) => option.name || ''}
                                    getOptionSelected={(option, value) => option && value ? option.name === value.name : ''}
                                    value={formik.values.site}
                                    onChange={handleSiteChange}
                                    onBlur={formik.handleBlur}
                                    size="small"
                                    required
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            className={classes.Required}
                                            label="Site"
                                            variant="outlined"
                                            required={true}
                                            error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                                            helperText={Boolean(formik.touched.site) && formik.errors.site}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {sitesLoading ? <CircularProgress color="inherit" size={15} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />}
                                />
                            </FormControl>
                            <FormControl className={classes.InputWrapper}>
                                <AutoComplete
                                    id="equipmentType"
                                    name="equipmentType"
                                    multiple
                                    disableCloseOnSelect
                                    disabled={isEmpty(formik.values.site)}
                                    options={equipmentTypeList}
                                    getOptionLabel={(option) => option.name || ''}
                                    getOptionSelected={(option, value) => option && value ? option.name === value.name : ''}
                                    value={formik.values.equipmentType}
                                    onChange={handleEquipmentTypeChange}
                                    onBlur={formik.handleBlur}
                                    size="small"
                                    renderTags={(value, getTagProps) => value.map((option, index) => {
                                        if (index < 2) {
                                            return (
                                                <Chip
                                                    classes={{
                                                        root: classes.Chip
                                                    }}
                                                    variant="outlined"
                                                    label={option.name}
                                                    {...getTagProps({ index })}
                                                />
                                            );
                                        } else if (index === 2) {
                                            return (<span className={classes.Tag}>{` + ${value.length - 2} Selected`}</span>);
                                        } else {
                                            return null;
                                        }
                                    })}
                                    renderOption={(option, { selected }) => (
                                        <React.Fragment>
                                            <Checkbox
                                                value={option.id}
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {option.name}
                                        </React.Fragment>
                                    )}
                                    required
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            className={classes.Required}
                                            label="Equipment Type"
                                            variant="outlined"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {equipmentLoading ? <CircularProgress color="inherit" size={15} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />}
                                />
                            </FormControl>
                            <FormControl className={Boolean(formik.errors.equipment) && Boolean(formik.touched.equipment) ? classes.ErrorWrapper : classes.InputWrapper}>
                                <AutoComplete
                                    id="equipment"
                                    name="equipment"
                                    multiple
                                    onBlur={handleBlur}
                                    disableCloseOnSelect
                                    disabled={isEmpty(formik.values.site) || bobLoading.includes('fetchEntityRules')}
                                    options={equipmentList}
                                    getOptionLabel={(option) => option.name || ''}
                                    getOptionSelected={(option, value) => option && value ? option.name === value.name : ''}
                                    value={formik.values.equipment}
                                    onChange={handleEquipmentChange}
                                    size="small"
                                    renderTags={(value, getTagProps) => value.map((option, index) => {
                                        if (index < 2) {
                                            return (
                                                <Chip
                                                    key={index}
                                                    classes={{
                                                        root: classes.Chip
                                                    }}
                                                    onMouseDown={(event) => {
                                                        event.stopPropagation();
                                                    }}
                                                    variant="outlined"
                                                    label={option.name}
                                                    onDelete={() => handleDeleteChip(option, 'equipment')}
                                                />
                                            );
                                        } else if (index === 2) {
                                            return (<span key={index} className={classes.Tag}>{` + ${value.length - 2} Selected`}</span>);
                                        } else {
                                            return null;
                                        }
                                    })}
                                    renderOption={(option, { selected }) => (
                                        <React.Fragment>
                                            <Checkbox
                                                key={option.id}
                                                value={option.id}
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {option.name}
                                        </React.Fragment>
                                    )}
                                    required={isEmpty(formik.values.system)}
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            className={classes.Required}
                                            label="Equipment"
                                            variant="outlined"
                                            required={isEmpty(formik.values.system)}
                                            error={Boolean(formik.errors.equipment) && Boolean(formik.touched.equipment)}
                                            helperText={Boolean(formik.touched.equipment) && formik.errors.equipment}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {equipmentLoading ? <CircularProgress color="inherit" size={15} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />}
                                />
                            </FormControl>
                            <FormControl className={Boolean(formik.errors.system) && Boolean(formik.touched.system) ? classes.ErrorWrapper : classes.InputWrapper}>
                                <AutoComplete
                                    id="system"
                                    name="system"
                                    multiple
                                    onBlur={handleBlur}
                                    disableCloseOnSelect
                                    disabled={isEmpty(formik.values.site) || bobLoading.includes('fetchEntityRules')}
                                    options={systemList}
                                    getOptionLabel={(option) => option.name || ''}
                                    getOptionSelected={(option, value) => option && value ? option.name === value.name : ''}
                                    value={formik.values.system}
                                    onChange={handleSystemChange}
                                    size="small"
                                    renderTags={(value, getTagProps) => value.map((option, index) => {
                                        if (index < 2) {
                                            return (
                                                <Chip
                                                    key={index}
                                                    classes={{
                                                        root: classes.Chip
                                                    }}
                                                    onMouseDown={(event) => {
                                                        event.stopPropagation();
                                                    }}
                                                    variant="outlined"
                                                    label={option.name}
                                                    onDelete={() => handleDeleteChip(option, 'system')}
                                                    
                                                />
                                            );
                                        } else if (index === 2) {
                                            return (<span key={index} className={classes.Tag}>{` + ${value.length - 2   } Selected`}</span>);
                                        } else {
                                            return null;
                                        }
                                    })}
                                    renderOption={(option, { selected }) => (
                                        <React.Fragment>
                                            <Checkbox
                                                key={option.id}
                                                value={option.id}
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {option.name}
                                        </React.Fragment>
                                    )}
                                    required={isEmpty(formik.values.equipment)}
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            className={classes.Required}
                                            label="System"
                                            variant="outlined"
                                            required={ isEmpty(formik.values.equipment)}
                                            error={Boolean(formik.errors.system) && Boolean(formik.touched.system)}
                                            helperText={Boolean(formik.touched.system) && formik.errors.system}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {equipmentLoading ? <CircularProgress color="inherit" size={15} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />}
                                />
                            </FormControl>
                        </div>
                        <div className={classes.TabPane}>
                            {
                                reportType === 'optimization' ?
                                    <OptimizationReportTabPane
                                        activeConfiguration={activeConfiguration}
                                        entities={entities}
                                        equipmentList={equipmentList}
                                        optimizationSensorsOrder={optimizationSensorsOrder}
                                        reportFilter={reportFilter}
                                        selectedTab={selectedOptimizationTab}
                                        setEntities={setEntities}
                                        setOptimizationSensorsOrder={setOptimizationSensorsOrder}
                                        setReportFilter={setReportFilter}
                                        site={formik.values.site}
                                        siteList={siteList}
                                        selectedClient={formik.values.client}
                                        selectedEquipment={formik.values.equipment}
                                        selectedSystems={formik.values.system}
                                        selectedRules={selectedRules}
                                        selectedSpaceAirSensors={selectedSpaceAirSensors}
                                        selectedOutsideAirSensors={selectedOutsideAirSensors}
                                        setSelectedRules={setSelectedRules}
                                        selectedState={selectedState}
                                        selectedStateItems={selectedStateItems}
                                        setSelectedState={setSelectedState}
                                        setSelectedStateItems={setSelectedStateItems}
                                        setSelectedSpaceAirSensors={setSelectedSpaceAirSensors}
                                        setSelectedTab={setSelectedOptimizationTab}
                                        setSelectedOutsideAirSensors={setSelectedOutsideAirSensors}
                                        setSelectedEquipments={setSelectedEquipments}
                                        setSelectedSystems={setSelectedSystems}
                                        systemList={systemList}
                                    />
                                    :
                                    <ReportTabPane
                                        getOccupiedHoursList={getOccupiedHoursList}
                                        isRunTimeDataValid={isRunTimeDataValid}
                                        runTimeData={runTimeData}
                                        selectedEquipment={formik.values.equipment}
                                        selectedSystems={formik.values.system}
                                        selectedRules={selectedRules}
                                        selectedSpaceAirSensors={selectedSpaceAirSensors}
                                        selectedOutsideAirSensors={selectedOutsideAirSensors}
                                        selectedOccupanyStatusSensors={selectedOccupanyStatusSensors}
                                        setSelectedRules={setSelectedRules}
                                        selectedState={selectedState}
                                        selectedStateItems={selectedStateItems}
                                        setSelectedState={setSelectedState}
                                        setSelectedStateItems={setSelectedStateItems}
                                        setSelectedSpaceAirSensors={setSelectedSpaceAirSensors}
                                        setSelectedOutsideAirSensors={setSelectedOutsideAirSensors}
                                        setSelectedOccupanyStatusSensors={setSelectedOccupanyStatusSensors}
                                        spaceAirSensorData={formik.errors.spaceAirSensors}
                                        setRunTimeData={setRunTimeData}
                                        outsideAirSensorData={formik.errors.outsideAirSensors}
                                        objectsToMonitor={objectsToMonitor}
                                        setObjectsToMonitor={setObjectsToMonitor}
                                        aggregate={aggregate}
                                        setAggregate={setAggregate}
                                        occupancyAggregate={occupancyAggregate}
                                        setOccupancyAggregate={setOccupancyAggregate}
                                        rulesTabDataState={rulesTabDataState}
                                        setRulesTabDataState={setRulesTabDataState}
                                        runTimeTabDataState={runTimeTabDataState}
                                        setRunTimeTabDataState={setRunTimeTabDataState}
                                        selectState={selectState}
                                        setSelectState={setSelectState}
                                        activeRadioButton={activeRadioButton}
                                        setActiveRadioButton={setActiveRadioButton}
                                        minFaultHours={minFaultHours}
                                        setMinFaultHours={setMinFaultHours}
                                    />
                            }
                        </div>
                    </Splitter>
                </div>
                <div className={classes.ButtonPane}>
                    <style>
                        {`
                            .k-menu-link-text {
                                font-size: 1.2rem;
                            }                      
                        `}
                    </style>
                    <SplitButton
                        className={classes.SplitButtons}
                        disabled={formik.values.timeRange === 'Custom Date' && (startDate === null || endDate === null)}
                        size={'small'}
                        items={buttonItems}
                        text="Save"
                        onButtonClick={handleButtonClick}
                        onItemClick={(e) => handleButtonItemClick(e)}
                    />
                    <BobCancelButton
                        className={classes.CancelButton}
                        onClick={handleCancel}
                        disabled={loading}
                        label={'Cancel'}
                    />
                </div>
            </Splitter>
        </React.Fragment>
    );
};

const mapStateToProps = state => {
    return {
        activeConfiguration:         state.configurationData.activeConfiguration,
        activeNavCompany:            state.bob.activeNavCompany,
        bobLoading:                  state.bob.bobLoading,
        currentUser:                 state.currentUser,
        isClearAppliedConfiguration: state.configurationData.isClearAppliedConfiguration,
        navClients:                  state.bob.navClients,
        navClientsMeta:              state.bob.navClientsMeta,
        newConfiguration:            state.configurationData.newConfiguration,
        updateConfigurationData:     state.configurationData.updateConfigurationData,
        workbookModeEnabled:         state.dashboard.workbookModeEnabled
    };
};

const mapDispatchToProps = {
    ...ConfigurationActions,
    ...DashboardActions,
    ...ReportingActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportConfigurationCreate);
